/**
 * Number input that allows clearing 0 while editing.
 *
 * Uses text + inputMode=decimal (not type=number) because controlled
 * type="number" inputs often refuse to become empty when the value is 0.
 *
 * `value` / `onChange` use storage units. Optional `scale` maps for display
 * (e.g. scale=100 shows 6.5 for stored 0.065).
 */
import { useEffect, useState } from 'react';
import { Input } from './ui/input';
import {
  toDisplayValue,
  draftOnFocus,
  commitDraft,
  isEffectivelyZero,
  reconcileDraft,
} from '../utils/clearableNumber';
import { preserveFieldSelection, selectFieldText } from '../utils/selectFieldText';

/** Allow empty, digits, one dot/comma, optional leading minus while typing. */
function isAllowedNumericDraft(raw) {
  if (raw === '' || raw === '-' || raw === '.' || raw === ',') return true;
  return /^-?\d*[.,]?\d*$/.test(raw);
}

export function ClearableNumberInput({
  value,
  onChange,
  scale = 1,
  step,
  className,
  min,
  max,
  id,
  name,
  'aria-label': ariaLabel,
  'data-testid': testId,
  disabled = false,
  readOnly = false,
}) {
  // null = not editing (show committed display); string = in-progress draft
  const [draft, setDraft] = useState(null);
  const display = toDisplayValue(value, scale);
  const editing = draft !== null;
  // Blank — not "0" — so focusing a new amount does not leave a leftover digit.
  const shown = editing ? draft : (isEffectivelyZero(display) ? '' : String(display));

  // Version / file switch replaces `value` from outside. Drop a stale draft
  // so the field shows the new version, not the previous keystrokes.
  useEffect(() => {
    setDraft((current) => reconcileDraft(current, value, scale));
  }, [value, scale]);

  const handleFocus = (e) => {
    if (disabled || readOnly) return;
    setDraft(draftOnFocus(display));
    selectFieldText(e);
  };

  const handleBlur = () => {
    if (disabled || readOnly) return;
    const raw = draft === null ? String(display) : draft;
    setDraft(null);
    onChange(commitDraft(raw, scale, { min, max }));
  };

  const handleChange = (e) => {
    if (disabled || readOnly) return;
    const raw = e.target.value.replace(',', '.');
    if (!isAllowedNumericDraft(raw)) return;
    setDraft(raw);

    // Propagate only complete numeric values — keep empty / "." local until blur
    if (raw === '' || raw === '-' || raw === '.' || raw.endsWith('.')) return;
    const parsed = parseFloat(raw);
    if (!Number.isFinite(parsed)) return;
    onChange(commitDraft(raw, scale, { min, max }));
  };

  const handleKeyDown = (e) => {
    // Parent row editors may handle Enter (save) and call preventDefault —
    // do not blur/steal focus in that case (avoids save races for automation).
    if (e.key === 'Enter' && !e.defaultPrevented) {
      e.currentTarget.blur();
    }
  };

  return (
    <Input
      id={id}
      name={name}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={shown}
      placeholder=""
      onChange={handleChange}
      onFocus={handleFocus}
      onMouseUp={disabled || readOnly ? undefined : preserveFieldSelection}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      readOnly={readOnly}
      aria-label={ariaLabel}
      data-testid={testId}
      className={className}
      data-step={step}
    />
  );
}
